import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import type { Profile } from '@/lib/types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Ensure we only attempt avatar sync once per user session
  const avatarSyncDoneForUserId = useRef<string | null>(null);

  // Helper to sync Google avatar to profile (non-blocking caller)
  const syncAvatarToProfile = async (userId: string, authAvatarUrl: string) => {
    try {
      const { data: existingProfile, error: readError } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('user_id', userId)
        .maybeSingle();

      if (readError) return;

      // Only update if avatar is missing or different
      if (!existingProfile?.avatar_url || existingProfile.avatar_url !== authAvatarUrl) {
        await supabase
          .from('profiles')
          .update({ avatar_url: authAvatarUrl })
          .eq('user_id', userId);
      }
    } catch {
      // Never block rendering on sync errors
      return;
    }
  };

  // Get avatar URL from auth metadata
  const getAuthAvatarUrl = (user: User): string | null => {
    return user?.user_metadata?.avatar_url || user?.user_metadata?.picture || null;
  };

  useEffect(() => {
    // Set up auth state listener BEFORE checking session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const authAvatarUrl = getAuthAvatarUrl(session.user);

          // Fire-and-forget: never await profile sync in the auth listener
          if (authAvatarUrl && avatarSyncDoneForUserId.current !== session.user.id) {
            avatarSyncDoneForUserId.current = session.user.id;
            void syncAvatarToProfile(session.user.id, authAvatarUrl);
          }

          // Fetch profile with setTimeout to avoid race condition
          setTimeout(async () => {
            try {
              const { data } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', session.user.id)
                .maybeSingle();

              setProfile(data as Profile | null);
            } catch {
              setProfile(null);
            }
          }, 0);
        } else {
          setProfile(null);
          avatarSyncDoneForUserId.current = null;
        }

        // Never block the UI here
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const authAvatarUrl = getAuthAvatarUrl(session.user);

        // Fire-and-forget sync
        if (authAvatarUrl && avatarSyncDoneForUserId.current !== session.user.id) {
          avatarSyncDoneForUserId.current = session.user.id;
          void syncAvatarToProfile(session.user.id, authAvatarUrl);
        }

        try {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .maybeSingle();

          setProfile(data as Profile | null);
        } catch {
          setProfile(null);
        }
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          display_name: displayName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
